import { Trans } from '@i18n';
import { Checkbox, FormControlLabel, FormGroup, Stack } from '@mui/material';
import { custom_field_entity_enum } from '@predium/enums';
import isEqual from 'lodash/isEqual';
import { memo, useCallback } from 'react';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { useBuildingFilters } from '@/components/building-filters/BuildingFiltersProvider';
import { findCustomFieldFilter, upsertCustomFieldFilter } from '@/components/building-filters/custom-field-filters';
import Collapsible from '@/components/common/Collapsible';

type Props = {
  entity: custom_field_entity_enum;
  fieldKey: string;
  title: string;
  options: string[];
  truncated: boolean;
};

function StringFilter({ entity, fieldKey, title, options, truncated }: Props) {
  const { filters, setFilters } = useBuildingFilters();

  const selected = findCustomFieldFilter(filters.customFields, entity, fieldKey)?.values ?? [];

  const handleToggle = useCallback(
    (value: string, isChecked: boolean) => {
      void setFilters((prev) => {
        const current = findCustomFieldFilter(prev.customFields, entity, fieldKey)?.values ?? [];
        const next = isChecked ? [...current, value] : current.filter((option) => option !== value);
        return {
          ...prev,
          customFields: upsertCustomFieldFilter(prev.customFields, entity, fieldKey, {
            values: next.length > 0 ? next : null,
          }),
        };
      });
    },
    [entity, fieldKey, setFilters],
  );

  return (
    <Collapsible title={title}>
      <Stack spacing={1} mt={-1}>
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 1,
            width: '100%',
          }}
        >
          {truncated ? (
            <PTypography variant="body2" color="secondary">
              <Trans i18nKey="DataCollection_BuildingListFilter-customFieldTooManyValues" />
            </PTypography>
          ) : null}
          {!truncated && options.length === 0 ? (
            <PTypography variant="body2" color="secondary">
              <Trans i18nKey="General_NoDataAvailable" />
            </PTypography>
          ) : null}
          {truncated
            ? null
            : options.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={selected.includes(option)}
                      onChange={(event) => handleToggle(option, event.target.checked)}
                    />
                  }
                  label={option}
                />
              ))}
        </FormGroup>
      </Stack>
    </Collapsible>
  );
}

export const CustomFieldStringFilter = memo(StringFilter, isEqual);
